/**
 * The public signup page's behaviour (/signup, PRE-72), in one hook both looks run (design record §7b rule 1: today's
 * src/pages/Signup.tsx and the new design's rebuild). Its words are SIGNUP_WORDS (src/components/auth/signupWords.ts);
 * its rules are src/lib/signup/signupValidation.ts.
 *
 * What a send does, in order:
 *   1. Firebase Auth `createUserWithEmailAndPassword` mints the user. No code redemption or trial purchase here; that
 *      belongs to /start.
 *   2. The typed name becomes the account's displayName.
 *   3. The branded verification email is requested (fire-and-forget).
 *   4. A consent audit doc is written at `prest0n_sms_consent/{uid}`; the `sendDoubleOptInSMS` callable stamps the
 *      trusted IP server-side and texts "Reply YES to confirm", only when the person opted in.
 *   5. Organic provisioning, then into onboarding; a held trial code goes to /start; a waiting team invitation goes to
 *      '/', where PendingInviteGate takes it.
 *   6. With SMS: the "check your phone" screen, which keeps the Resend button reachable. Without: straight on.
 */
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { requestBrandedVerificationEmail } from '../lib/identity/signInOrLink';
import { recordSignupWhileSignedIn } from '../utils/signOutBreadcrumb';
import { isSignupSubmittable, normalizePhoneE164, validatePassword } from '../lib/signup/signupValidation';
import { SIGNUP_WORDS } from '../components/auth/signupWords';

const E = SIGNUP_WORDS.errors;
const R = SIGNUP_WORDS.checkPhone;

export type SignupFormState = {
  name: string;
  email: string;
  phone: string;
  firmName: string;
  password: string;
  smsConsent: boolean;
  tosAccepted: boolean;
};

export const INITIAL_SIGNUP_FORM: SignupFormState = {
  name: '',
  email: '',
  phone: '',
  firmName: '',
  password: '',
  smsConsent: false,
  tosAccepted: false,
};

/** Which screen the page shows: the form, "account created" (no SMS), or "check your phone" (SMS). */
export type SignupScreen = 'form' | 'created' | 'check-phone';

export function useSignupForm() {
  const navigate = useNavigate();
  // PR-F0 A3 (2026-09-21): the 01:08Z sign-out on /desktop was preceded by a
  // `/signup` navigation while signed in that nothing explained. Record the
  // mount for an already signed-in (non-anonymous) person — diagnostics only.
  useEffect(() => {
    try {
      const current = getAuth().currentUser;
      if (current && !current.isAnonymous) recordSignupWhileSignedIn();
    } catch { /* diagnostics never throw */ }
  }, []);
  const [form, setForm] = useState<SignupFormState>(INITIAL_SIGNUP_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneUid, setDoneUid] = useState<string | null>(null);
  // Whether the user opted into SMS at submit time. Determines which success
  // screen to render (full double-opt-in flow vs. simple "account created").
  const [doneSmsOptedIn, setDoneSmsOptedIn] = useState<boolean>(false);
  // Where "continue to setup" goes from the check-your-phone screen — kept
  // on screen (not auto-navigated) so the Resend affordance stays reachable.
  const [doneTarget, setDoneTarget] = useState<string>('/onboarding');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Derived: is submit enabled? Terms box + all required fields + valid
  // phone + valid password. Firm is optional.
  const formValid = useMemo(() => isSignupSubmittable(form), [form]);

  const updateField = <K extends keyof SignupFormState>(key: K, value: SignupFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !formValid) return;

    setError(null);
    setSubmitting(true);

    const phoneE164 = normalizePhoneE164(form.phone);
    if (!phoneE164) {
      setError(E.phone);
      setSubmitting(false);
      return;
    }

    const pwCheck = validatePassword(form.password);
    if (!pwCheck.ok) {
      setError(pwCheck.reason || E.passwordGeneric);
      setSubmitting(false);
      return;
    }

    try {
      const auth = getAuth();
      // 1) Create the Firebase Auth user.
      const cred = await createUserWithEmailAndPassword(auth, form.email.trim().toLowerCase(), form.password);
      const uid = cred.user.uid;

      // The typed full name becomes the account's displayName (2026-08-25):
      // this page was the ONLY surface capturing a real name, and it used to
      // strand it in the consent doc — downstream identity (onboarding
      // prefill, "[first name]'s Workspace") reads auth displayName.
      try {
        const { updateProfile } = await import('firebase/auth');
        const typedName = form.name.trim().replace(/\s+/g, ' ');
        if (typedName) await updateProfile(cred.user, { displayName: typedName });
      } catch { /* non-fatal — onboarding re-captures the name */ }

      // Best-effort email verification (non-fatal).
      // Branded verification email (task #50) — fire-and-forget; the shared
      // helper falls back to Firebase's default template if the callable is
      // unreachable (2026-08-14: consolidated from an inline duplicate).
      void requestBrandedVerificationEmail(cred.user);

      // 2) Write consent audit doc. smsConsentIpAddress is intentionally left
      //    for the callable to stamp server-side (trusted header).
      const db = getFirestore();
      await setDoc(doc(db, 'prest0n_sms_consent', uid), {
        uid,
        email: form.email.trim().toLowerCase(),
        phone: phoneE164,
        name: form.name.trim(),
        firmName: form.firmName.trim() || null,
        smsConsent: form.smsConsent === true,
        smsConsentTimestamp: serverTimestamp(),
        smsConsentUserAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        tosAccepted: true,
        tosAcceptedTimestamp: serverTimestamp(),
        doubleOptInStatus: form.smsConsent === true ? 'pending' : 'not_opted_in',
        source: 'prest0.ai/signup',
      });

      // 3) Fire the double-opt-in callable ONLY when the user opted in.
      //    SMS consent is optional (Twilio A2P rule 30923) — do not send a
      //    confirmation text to someone who did not check the SMS box.
      if (form.smsConsent === true) {
        try {
          const fns = getFunctions(undefined, 'us-central1');
          const callable = httpsCallable<{ uid: string }, { ok: boolean }>(fns, 'sendDoubleOptInSMS');
          await callable({ uid });
        } catch (callErr: any) {
          // Do not block the UX — the user still has a pending consent doc we can
          // retry via the Resend button.
          // eslint-disable-next-line no-console
          console.warn('[signup] sendDoubleOptInSMS failed (non-fatal):', callErr?.message || callErr);
        }
      }

      // 4) Organic provisioning + into onboarding (task #74: EVERY
      //    account-creation path gets the same staged onboarding — this page
      //    used to dead-end on a static "account created" screen). Review
      //    round 2026-08-18: code/team-invite holders are EXCLUDED — same
      //    contract as the sign-in form — and refusal reasons route honestly.
      let pendingCode = false;
      try { pendingCode = Boolean(sessionStorage.getItem('pending_trial_code')); } catch { /* default organic */ }
      let target = '/onboarding';
      if (pendingCode) {
        // A held trial code belongs to the /start redemption journey — no
        // founder-org provisioning here.
        target = '/start';
      } else {
        try {
          const fns = getFunctions(undefined, 'us-central1');
          const startOrganicTrial = httpsCallable(fns, 'startOrganicTrial');
          const result: any = await startOrganicTrial({});
          const r = (result as any)?.data || {};
          if (r?.started === false && r?.reason === 'pending-team-invite') {
            // A team invitation is waiting — '/' lets PendingInviteGate
            // funnel them into their team instead of founder onboarding.
            target = '/';
          } else if (r?.started === false && r?.reason === 'is-workspace-member') {
            target = '/';
          }
        } catch (e) {
          console.warn('[signup] startOrganicTrial failed (non-fatal):', e);
        }
      }
      // Prefill handoff: the company + phone typed here must not be retyped
      // in onboarding (review round 2026-08-18).
      try {
        sessionStorage.setItem('prest0_signup_prefill', JSON.stringify({
          company: form.firmName || '', phone: form.phone || '',
        }));
      } catch { /* prefill is best-effort */ }
      setDoneSmsOptedIn(form.smsConsent === true);
      setDoneUid(uid);
      if (form.smsConsent === true) {
        // Keep the "check your phone" screen reachable — it carries the
        // Resend button for a failed double-opt-in SMS; its primary CTA
        // continues into the journey.
        setDoneTarget(target);
      } else {
        navigate(target);
      }
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        setError(E.emailInUse);
      } else if (code === 'auth/weak-password') {
        setError(E.weakPassword);
      } else if (code === 'auth/invalid-email') {
        setError(E.invalidEmail);
      } else {
        setError(E.generic);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!doneUid || resending) return;
    setResending(true);
    setResendMessage(null);
    try {
      const fns = getFunctions(undefined, 'us-central1');
      const callable = httpsCallable<{ uid: string }, { ok: boolean }>(fns, 'sendDoubleOptInSMS');
      await callable({ uid: doneUid });
      setResendMessage(R.resent);
    } catch {
      setResendMessage(R.resendFailed);
    } finally {
      setResending(false);
    }
  };

  // Two success screens depending on whether the user opted into SMS:
  //   - SMS opt-in → "Check your phone" double-opt-in flow.
  //   - No SMS    → "Account created" simple confirmation (no Twilio send).
  const screen: SignupScreen = !doneUid ? 'form' : doneSmsOptedIn ? 'check-phone' : 'created';

  /** "Sign in to your new account", from either success screen. */
  const goSignIn = () => navigate('/auth', { state: { returnUrl: '/signup' } });
  /** "continue to setup →" from the check-your-phone screen. */
  const continueToSetup = () => navigate(doneTarget);

  return {
    form,
    updateField,
    formValid,
    submitting,
    error,
    screen,
    handleSubmit,
    handleResend,
    resending,
    resendMessage,
    goSignIn,
    continueToSetup,
  };
}
